import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import Icons from '~/Common-Objects/UI/Icons/index'
import LoadingIcons from '~/Common-Objects/LoadingIcons/loadingIcon1'
import ProgressNavSmall from '~/Common-Objects/Navigation/ProgressNavSmall'
import TextInput from '~/Common-Objects/UI/Forms/TextInput/TextInput'
import i18n from '~/i18n'
import EmailValidator from '~/Classes/emailValidator'
import axios from '~/data/http/axios'
import postHogFunctions from '~/Classes/posthog'
import { parse } from 'tldts'

export default function AddDomain({
    onSuccess = () => {},
    isVerifying = false,
    originalEmailAddress = '',
    initialEmailAddress = ''
}) {
    const [emailAddress, setEmailAddress] = useState(originalEmailAddress)
    const [emailAddressError, setEmailAddressError] = useState('')
    const [awaitingVerification, setAwaitingVerification] = useState(isVerifying)
    const [lastSentVerificationEmail, setLastSentVerificationEmail] = useState(new Date())
    const [domainName, setDomainName] = useState('')
    const [domainNameError, setDomainNameError] = useState('')
    const [masterDomainName, setMasterDomainName] = useState('')
    const [addedDomainName, setAddedDomainName] = useState(false)
    const [showResendButton, setShowResendButton] = useState(true)
    const [doneVerification, setDoneVerification] = useState(false)
    const [loading, setLoading] = useState(false)
    const verificationTimerRef = useRef(null)
    const [stepNavNo, setStepNavNo] = useState(1)
    useEffect(() => {
        if (initialEmailAddress) {
            setEmailAddress(initialEmailAddress)
            let tmpDomainName = initialEmailAddress.split('@')[0]
            setDomainName(tmpDomainName)
        }
        // Cleanup timer on unmount
        return () => {
            if (verificationTimerRef.current) {
                clearInterval(verificationTimerRef.current)
                verificationTimerRef.current = null
            }
        }
    }, [])

    const checkDomainName = async () => {
        setDomainNameError('')
        let domain = domainName
        if (!domain) {
            setDomainNameError('Please enter a domain name')
            return
        }

        if (domain.indexOf('https://') === 0) {
            // strip and continue
            domain = domain.replace('https://', '')
            setDomainName(domain)
        }
        if (domain.indexOf('http://') === 0) {
            // strip and continue
            domain = domain.replace('http://', '')
            setDomainName(domain)
        }
        if (domain.indexOf('www.') === 0) {
            // strip and continue
            domain = domain.replace('www.', '')
            setDomainName(domain)
        }

        if (
            domain.indexOf('http://') > -1 ||
            domain.indexOf('https://') > -1 ||
            domain.indexOf('www.') > -1 ||
            domain.indexOf('.') == -1 ||
            domain.indexOf(' ') != -1 ||
            domain.indexOf('.') == 0 ||
            domain.indexOf('.') == domain.length - 1
        ) {
            setDomainNameError('Please enter a valid domain name')
            return
        }
        const response = await axios.post(`/accountMaster/domain/checkDomainName`, {
            domainName: domain
        })
        if (response.data?.exists) {
            setDomainNameError('Domain already exists')
            return
        }
        let tmpDomainName = domain
        if (tmpDomainName?.indexOf('@') > -1) {
            tmpDomainName = tmpDomainName.split('@')[1]
        }

        //is it a sub domain
        const parsed = parse(tmpDomainName)
        if (parsed.domain != tmpDomainName) {
            setMasterDomainName(parsed.domain)
        }
        setStepNavNo(2)
        setDomainName(tmpDomainName)
        setAddedDomainName(true)
    }
    const sendVerificationEmail = async (retry = false) => {
        setEmailAddressError('')
        setLoading(true)
        if (!EmailValidator.validateEmail(emailAddress)) {
            setEmailAddressError(i18n.t('company:domains.pleaseEnterAValidEmail'))
            setLoading(false)
            return
        }

        try {
            const addResponse = await axios.post(`/accountMaster/domain?retry=${retry ? 1 : 0}`, {
                emailAddress: emailAddress.toLowerCase(),
                domainName: domainName.toLowerCase()
            })
            if (addResponse.data.Required == false) {
                setLoading(false)
                setEmailAddressError(i18n.t('company:domains.alertDomainCreate'))
            } else {
                let domainId = addResponse.data.domain.id
                setAwaitingVerification(true)
                setShowResendButton(false)
                setLastSentVerificationEmail(new Date())
                checkingVerification(domainId)
                postHogFunctions.capture('authenticate_domain_button_domain_verification_sent')
                setLoading(false)
            }
        } catch (err) {
            setEmailAddressError(err.data.error)
            setLoading(false)
        }
    }

    const checkingVerification = async (domainId) => {
        // Clear any existing timer first
        if (verificationTimerRef.current) {
            clearInterval(verificationTimerRef.current)
        }

        verificationTimerRef.current = setInterval(async () => {
            try {
                let response = await axios.get(`/accountMaster/domain/` + domainId)
                if (response.data.verified) {
                    postHogFunctions.capture('my_domains_domain_verified')
                    setDoneVerification(true)
                    setAwaitingVerification(false)
                    if (verificationTimerRef.current) {
                        clearInterval(verificationTimerRef.current)
                        verificationTimerRef.current = null
                    }
                    onSuccess(domainId)
                } else {
                    if (
                        lastSentVerificationEmail &&
                        lastSentVerificationEmail < new Date(new Date().getTime() - 1000 * 10)
                    ) {
                        setShowResendButton(true)
                    }
                }
            } catch (error) {
                console.error('Error checking verification:', error)
            }
        }, 2000)
    }

    if (doneVerification) {
        return <></>
    }

    return (
        <>
            <ProgressNavSmall steps={2} step={stepNavNo} className='mar-b36' />
            <h2 className='mar-b15 text-center'>{i18n.t('company:domains.addDomainHeader')}</h2>

            {awaitingVerification && (
                <>
                    <div
                        className='mar-b40 text-center text-grey-800'
                        style={{ lineHeight: '1.5' }}
                        dangerouslySetInnerHTML={{
                            __html: i18n.t('company:domains.addDomainSubHeader2Verification', {
                                domainname: masterDomainName
                                    ? `either <strong style="color: var(--neutral-950); font-weight: 600;">${domainName}</strong> or <strong style="color: var(--neutral-950); font-weight: 600;">${masterDomainName}</strong>`
                                    : `<strong style="color: var(--neutral-950); font-weight: 600;">${domainName}</strong>`
                            })
                        }}
                    />
                    <div className='mar-b25'>
                        <LoadingIcons iconType='2' style={{ width: 32, height: 32, top: 0 }} />
                    </div>
                    <h4 className='mar-b12'>{i18n.t('company:domains.addDomainSentVerifyEmail')}</h4>
                    <div className='text-sml text-center text-grey mar-b5'>
                        {i18n.t('company:domains.verificationLinkSentTo')}{' '}
                        <strong style={{ color: 'var(--neutral-950)' }}>{emailAddress}</strong>.{' '}
                        {i18n.t('company:domains.addDomainSentVerifyEmailText2')}
                    </div>
                    <div className='text-sml text-center text-grey'>{i18n.t('company:domains.addDomainHelpLine')}</div>

                    {showResendButton && (
                        <div className='mar-t25'>
                            <Button
                                className='button-primary button-sml'
                                text={'Resend Verification Email'}
                                iconRight={<Icons icon='arrowRight' />}
                                onClick={() => sendVerificationEmail(true)}
                            />
                        </div>
                    )}
                </>
            )}

            {!awaitingVerification && (
                <div style={{ width: '100%' }}>
                    {!addedDomainName && (
                        <>
                            <div className='mar-b40 text-center text-grey-800' style={{ lineHeight: '1.5' }}>
                                {i18n.t('company:domains.addDomainSubHeader2')}
                            </div>
                            <div className='mar-b25'>
                                <TextInput
                                    name='domainName'
                                    label={i18n.t('company:domains.domainNameLabel')}
                                    placeholder={'example.com'}
                                    error={domainNameError}
                                    value={domainName}
                                    onChange={(e) => {
                                        setDomainName(e.target.value)
                                    }}
                                    onKeyUp={(e) => {
                                        if (e.key === 'Enter') {
                                            checkDomainName(e)
                                        } else {
                                            setDomainNameError('')
                                        }
                                    }}
                                    loading={loading}
                                />
                            </div>
                            <Button
                                className='button-primary'
                                disabled={loading}
                                text={'Continue'}
                                iconRight={<Icons icon='arrowRight' />}
                                onClick={checkDomainName}
                            />
                        </>
                    )}

                    {addedDomainName && (
                        <>
                            <div
                                className='mar-b40 text-center text-grey-800'
                                style={{ lineHeight: '1.5' }}
                                dangerouslySetInnerHTML={{
                                    __html: i18n.t('company:domains.addDomainSubHeader2Verification', {
                                        domainname: masterDomainName
                                            ? `either <strong style="color: var(--neutral-950); font-weight: 600;">${domainName}</strong> or <strong style="color: var(--neutral-950); font-weight: 600;">${masterDomainName}</strong>`
                                            : `<strong style="color: var(--neutral-950); font-weight: 600;">${domainName}</strong>`
                                    })
                                }}
                            />
                            <div className='mar-b25'>
                                <TextInput
                                    name='emailAddress'
                                    label={
                                        masterDomainName
                                            ? i18n.t('company:domains.emailAddressLabelMulti')
                                            : i18n.t('company:domains.emailAddressLabel')
                                    }
                                    placeholder={'emailaddress@' + (masterDomainName ? masterDomainName : domainName)}
                                    error={emailAddressError}
                                    value={emailAddress}
                                    onChange={(e) => setEmailAddress(e.target.value)}
                                    onKeyUp={(e) => {
                                        if (e.key === 'Enter') {
                                            sendVerificationEmail()
                                        } else {
                                            setEmailAddressError('')
                                        }
                                    }}
                                    loading={loading}
                                />
                            </div>
                            <Button
                                className='button-primary'
                                disabled={loading}
                                text={i18n.t('company:domains.sendVerificationEmail')}
                                iconRight={<Icons icon='arrowRight' />}
                                onClick={sendVerificationEmail}
                            />
                        </>
                    )}
                </div>
            )}
        </>
    )
}
